import * as React from 'react';

import {
  Slider as AriaSlider,
  SliderOutput as AriaSliderOutput,
  SliderOutputProps as AriaSliderOutputProps,
  SliderProps as AriaSliderProps,
  SliderStateContext as AriaSliderStateContext,
  SliderThumb as AriaSliderThumb,
  SliderThumbProps as AriaSliderThumbProps,
  SliderTrack as AriaSliderTrack,
  SliderTrackProps as AriaSliderTrackProps,
  SliderTrackRenderProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
  type SliderState,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { FieldError, Label, labelVariants } from '../Field';

const SliderOutput = ({ className, ...props }: AriaSliderOutputProps) => (
  <AriaSliderOutput
    className={cn('@SliderOutput', labelVariants(), className)}
    {...props}
  />
);

const Slider = ({
  className,
  orientation = 'horizontal',
  ...props
}: AriaSliderProps) => (
  <AriaSlider
    className={composeRenderProps(className, (className) =>
      cn(
        '@Slider',
        'relative flex touch-none items-center select-none',
        {
          'h-full': orientation === 'vertical',
          'w-full': orientation === 'horizontal',
        },
        className,
      ),
    )}
    orientation={orientation}
    {...props}
  />
);

const SliderTrack = ({ className, ...props }: AriaSliderTrackProps) => (
  <AriaSliderTrack
    className={composeRenderProps(
      cn('@SliderTrack', className),
      (className, renderProps: SliderTrackRenderProps) =>
        cn(
          {
            'h-2 w-full': renderProps.orientation === 'horizontal',
            'h-full w-2': renderProps.orientation === 'vertical',
          },
          'relative grow rounded-full bg-foreground/20',
          /* Disabled */
          'rac-disabled:opacity-50',
          className,
        ),
    )}
    {...props}
  />
);

const SliderFillTrack = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const state = React.useContext(AriaSliderStateContext) as SliderState | null;
  const orientation = state?.orientation === 'vertical' ? 'height' : 'width';
  return (
    <div
      style={{ [orientation]: (state?.getThumbPercent(0) ?? 0) * 100 + '%' }}
      className={cn(
        '@SliderFillTrack',
        'absolute rounded-full bg-primary',
        {
          'h-full': state?.orientation === 'horizontal',
          'bottom-0 w-full': state?.orientation === 'vertical',
        },
        className,
      )}
      {...props}
    />
  );
};

const SliderThumb = ({ className }: AriaSliderThumbProps) => (
  <AriaSliderThumb
    className={composeRenderProps(className, (className) =>
      cn(
        '@SliderThumb',
        'ring-offset-background top-1/2 left-1/2 block size-5 rounded-full border-2 border-primary bg-space transition-colors',
        /* Disabled */
        'rac-disabled:pointer-events-none',
        /* Focus Visible */
        'rac-focus-visible:ring-2 rac-focus-visible:ring-primary rac-focus-visible:ring-offset-2 rac-focus-visible:outline-none',
        className,
      ),
    )}
  />
);

interface SliderFieldProps extends AriaSliderProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  showOutput?: boolean;
  outputLabel?: string;
}

const SliderField = ({
  label,
  description,
  errorMessage,
  showOutput = true,
  // outputLabel = 'Value:',
  className,
  children,
  ...props
}: SliderFieldProps) => (
  <div className="@SliderField flex flex-col gap-2.5">
    <div>
      <Slider
        className={composeRenderProps(className, (className) =>
          cn('flex w-full flex-col items-start gap-2.5', className),
        )}
        {...props}
      >
        <div className="flex w-full justify-between">
          {label && <Label>{label}</Label>}
          {showOutput && <SliderOutput />}
        </div>
        <SliderTrack>
          <SliderFillTrack />
          <SliderThumb />
        </SliderTrack>
      </Slider>
    </div>
    {typeof children === 'function' ? null : children}
    {description && (
      <Text className="text-sm text-foreground/20" slot="description">
        {description}
      </Text>
    )}
    <FieldError>{errorMessage}</FieldError>
  </div>
);

export {
  Slider,
  SliderTrack,
  SliderFillTrack,
  SliderThumb,
  SliderOutput,
  SliderField,
};
export type { SliderFieldProps };
